import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import { updateTask } from "../../services/tachesServices";
import { getAdminsAndMembers } from "../../services/userServices";
import Button from "../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const STATUSES = ["Urgent", "En cours", "A faire", "Effectué"];

const initialForm = {
  categorie: "",
  titre: "",
  contenu: "",
  user_id: "",
  statut: "A faire",
};

const EditTaskModal = ({ isOpen, onClose, refreshList, task }) => {
  const [formData, setFormData] = useState(initialForm);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Alimente le formulaire quand la tâche change / ouverture modale
  useEffect(() => {
    if (task && isOpen) {
      setFormData({
        categorie: task.categorie || "",
        titre: task.titre || "",
        contenu: task.contenu || "",
        user_id: task.user_id || "",
        statut: task.statut || "A faire",
      });
    }
  }, [task, isOpen]);

  // Charge les utilisateurs (admins + membres) à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    const fetchUsers = async () => {
      try {
        setIsLoadingUsers(true);
        const response = await getAdminsAndMembers();
        setUsers(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Erreur lors du chargement des utilisateurs:", error);
        toast.error("Erreur lors du chargement des utilisateurs.");
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [isOpen]);

  // Reset quand on ferme
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialForm);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) =>
      (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
    );
  }, [users]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      contenu: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!task?.id) {
      toast.error("Tâche introuvable.");
      return;
    }

    if (!formData.user_id) {
      toast.error("Merci de sélectionner un utilisateur.");
      return;
    }

    try {
      setIsSubmitting(true);
      await updateTask(task.id, formData);

      if (refreshList) {
        await refreshList();
      }

      toast.success("Tâche mise à jour avec succès");
      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la tâche", error);
      toast.error("Erreur lors de la mise à jour de la tâche");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
      <div className="relative w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/70 dark:border-slate-700/70">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide font-montserrat">
            Modifier une tâche
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <form
          onSubmit={handleSubmit}
          className="px-4 sm:px-6 py-4 space-y-6 text-slate-800 dark:text-slate-100"
        >
          {/* Infos générales */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Informations générales
            </h4>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">Catégorie</label>
                <input
                  type="text"
                  name="categorie"
                  placeholder="Catégorie"
                  value={formData.categorie}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">Statut</label>
                <select
                  name="statut"
                  value={formData.statut}
                  onChange={handleChange}
                  className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">Titre de la tâche</label>
              <input
                type="text"
                name="titre"
                placeholder="Titre"
                value={formData.titre}
                onChange={handleChange}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">Attribuée à</label>
              {isLoadingUsers ? (
                <div className="text-xs text-slate-500 dark:text-slate-300">
                  Chargement des utilisateurs…
                </div>
              ) : (
                <select
                  name="user_id"
                  value={formData.user_id}
                  onChange={handleChange}
                  className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                >
                  <option value="">Sélectionnez un utilisateur</option>
                  {sortedUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {(u.nom || "").toUpperCase()} {u.prenom || ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Description détaillée */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Description détaillée
            </h4>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
              <ReactQuill
                value={formData.contenu}
                onChange={handleContentChange}
                theme="snow"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2 pb-4">
            <Button
              text={isSubmitting ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className="btn-primary gap-2"
              disabled={isSubmitting || isLoadingUsers}
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={handleCancel}
              type="button"
              className="btn-ghost gap-2"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTaskModal;
